import { useState, useEffect, useRef } from 'react';
import DataTable, { useDataTableHandle } from '../DataTable';
import { ExternalLinkAlt } from '@styled-icons/fa-solid';
import { Container } from 'react-bootstrap';
import isoDateConverter from '../isoDateConverter';
import CustomsTechModal from './CustomsTechModal';
import { commCodeRulesSearchLazy, postCommCodeRules } from '../apiCalls';
import { useSecurity } from '../Security/security';

const CustomsTech = () => {
  const { validateRole } = useSecurity();
  const canEditCustomsTechRules = validateRole({
    or: [
      { department: 'OPS', role: 'STANDARD' },
      { department: 'OPS', role: 'SUPERUSER' },
      { department: 'QA', role: 'SUPERUSER' },
      { department: 'CMT' },
    ],
  });
  //State & Refs
  const [paginationLoaded, setPaginationLoaded] = useState(false);
  const [selectedCustomTech, setSelectedCustomTech] = useState(null);
  const [customTechData, setCustomTechData] = useState([]);
  const [formData, setFormData] = useState({
    hsCode: '',
    origin: '',
    description: '',
    disabled: false,
    id: null,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorText, setErrorText] = useState('');
  const [persist, setPersist] = useState({});
  const formRef = useRef(null);
  const dth = useDataTableHandle();

  // Effects & Hooks
  useEffect(() => {
    if (paginationLoaded) fetchData();
  }, [paginationLoaded]);

  useEffect(() => {
    if (selectedCustomTech) {
      setFormData({
        hsCode: selectedCustomTech.hsCode?.trim?.() || '',
        origin: selectedCustomTech.origin?.trim?.() || '',
        description: selectedCustomTech.description?.trim?.() || '',
        disabled: selectedCustomTech.disabled || false,
        id: selectedCustomTech?.id || -1,
      });
      setIsEditing(selectedCustomTech && Object.keys(selectedCustomTech).length > 0);
    }
  }, [selectedCustomTech]);

  // Event Handlers & Functions
  async function fetchData() {
    try {
      const objectToSend = {
        skip: dth.current.pagination.pageNo * dth.current.pagination.itemsPerPage,
        amount: dth.current.pagination.itemsPerPage,
        sort_by: dth.current.sortStatus.currentKey,
        sort_order: dth.current.sortStatus.direction,
      };
      const data = await commCodeRulesSearchLazy(objectToSend);
      dth.current.setServerDataLength(data.totalItems);
      setCustomTechData(data.data);
    } catch (err) {
      console.log('Error retrieving data');
    }
  }

  async function updateData(setPaginationLength, pagination, sortStatus, search, filter) {
    try {
      const data = await commCodeRulesSearchLazy({
        skip: pagination.pageNo * pagination.itemsPerPage,
        amount: pagination.itemsPerPage,
        sort_by: sortStatus.currentKey,
        sort_order: sortStatus.direction,
        search,
        filter,
      });
      setPaginationLength(data.totalItems);
      setCustomTechData(data.data);
    } catch (err) {
      console.log('Error retrieving data');
    }
  }

  const handleInputChange = (event, field) => {
    const value = field === 'disabled' ? event.target.checked : event.target.value;

    // Validate input based on field type
    if (value.length > 0) {
      const validationPatterns = {
        hsCode: /^[0-9*]+$/, // only numbers and *
        origin: /^[a-zA-Z*]+$/, // only letters and *
      };

      if (validationPatterns[field] && !validationPatterns[field].test(value)) {
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setErrorText('');

    // Validate required fields
    const requiredFields = ['hsCode', 'origin', 'description'];
    const missingFields = requiredFields.filter((field) => !formData[field]);

    if (missingFields.length > 0) {
      setFieldErrors(
        requiredFields.reduce(
          (errors, field) => ({
            ...errors,
            [field]: !formData[field],
          }),
          {}
        )
      );
      setErrorText('Please fill in all required fields');
      return;
    }

    // Validate HS Code format
    if (formData.hsCode.length < 10 && !formData.hsCode.endsWith('*')) {
      setFieldErrors({ hsCode: true });
      setErrorText('HS Code must be at 10 characters long or end with a * symbol');
      return;
    }

    // Submit form data
    const response = await postCommCodeRules(formData, isEditing);

    if (response[0].valid) {
      handleModalClose();
    } else {
      setErrorText("There's already a rule with the same HS Code and Origin");
      setFieldErrors({ hsCode: true, origin: true });
    }
  };

  const handleModalClose = () => {
    fetchData();
    setSelectedCustomTech(null);
    setFormData({
      hsCode: '',
      origin: '',
      description: '',
      disabled: false,
      id: null,
    });
    setErrorText('');
    setFieldErrors({});
  };

  // Return Statement with Render Logic
  return (
    <>
      <CustomsTechModal
        show={selectedCustomTech}
        onHide={handleModalClose}
        customTechData={selectedCustomTech}
        formData={formData}
        handleInputChange={handleInputChange}
        fieldErrors={fieldErrors}
        errorText={errorText}
        isEditing={isEditing}
        formRef={formRef}
        handleSave={handleSave}
      />
      <Container className="container-md">
        <section className="floating-box" style={{ maxWidth: '1600px' }}>
          <h1 className="floating-box-header-general">Customs Tech</h1>
          <div className="px-3 pt-4">
            <DataTable
              handle={dth}
              lazyLoading
              filterable
              sortable
              data={customTechData}
              className="table-striped"
              advancedSearch
              showAdvnSearch={true}
              onUpdate={updateData}
              onPaginationLoaded={() => {
                setPaginationLoaded(true);
              }}
              showLowerCaseHeaders={true}
              persistGetter={persist}
              persistSetter={setPersist}
            >
              <DataTable.Head>
                <section className="d-flex justify-content-end datatable-button">
                  <button
                    type="button"
                    className="stepper-button yellow"
                    disabled={!canEditCustomsTechRules}
                    onClick={() => {
                      if (canEditCustomsTechRules) {
                        setSelectedCustomTech({});
                      }
                    }}
                  >
                    New
                  </button>
                </section>
              </DataTable.Head>
              <DataTable.Pagination itemsPerPage={[10, 15, 50]} areaPadding={'0px'} />
              <DataTable.Headers />
              <DataTable.Column colKey="hsCode" header="HS Code" searchOpts={{ type: 'text', sendAs: 'hsCode' }} />
              <DataTable.Column
                colKey="origin"
                header="Country of Origin"
                searchOpts={{ type: 'text', sendAs: 'origin' }}
              />
              <DataTable.Column
                colKey="description"
                header="Description"
                searchOpts={{ type: 'text', sendAs: 'description' }}
              >
                {(item) => {
                  return item.description?.slice(0, 50) + (item.description?.length > 50 ? '...' : '');
                }}
              </DataTable.Column>
              <DataTable.Column
                colKey="lastmodified"
                header="Last Updated"
                searchOpts={{ type: 'text', sendAs: 'lastModified' }}
              >
                {(item) => {
                  return isoDateConverter(item.lastmodified);
                }}
              </DataTable.Column>
              <DataTable.Column colKey="disabled" header="Disabled" searchOpts={{ type: 'text', sendAs: 'disabled' }}>
                {(item) => {
                  return item.disabled ? 'Yes' : 'No';
                }}
              </DataTable.Column>
              <DataTable.Column
                header="Edit"
                style={{ height: '1px', padding: 0 }}
                headerStyle={{ textAlign: 'center' }}
              >
                {(item) => (
                  <button
                    className={canEditCustomsTechRules ? 'external-table-link' : 'external-table-link-disabled'}
                    disabled={!canEditCustomsTechRules}
                    onClick={() => {
                      if (canEditCustomsTechRules) {
                        setSelectedCustomTech(item);
                      }
                    }}
                  >
                    <ExternalLinkAlt width="1.1rem" height="1.1rem" color="#00857d" />
                  </button>
                )}
              </DataTable.Column>
            </DataTable>
          </div>
        </section>
      </Container>
    </>
  );
};

export default CustomsTech;
